import dayjs from 'dayjs';

import type { SupplierDesk, SupplierDeskRow, SupplierEvent } from '../api/purchaseDesk';
import type { VendorLedger } from '../api/vendorPayments';
import { paymentMethodLabel } from '../bookings/paymentWords';
import type { SupplierEventKind } from '../generated/enums';
import { formatAmount } from '../format/money';

/**
 * The purchase desk's words (Option A of the design canvas, 2026-10-22): what each supplier is
 * waiting on, each paper in a line, and the quick amounts the Pay screen offers. The figures are
 * the server's; nothing here adds anything up but what the ledger already lists.
 */

const rupees = (value: number) => `₹${formatAmount(value)}`;
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
const day = (value: string) => dayjs(value.slice(0, 10)).format('D MMM');

/** The waiting steps on one line — "4 to receive · 2 to bill · ₹18,000 to pay" — or that nothing is. */
export function deskNeeds(row: SupplierDeskRow): string {
  const parts = [
    row.toReceive > 0 ? `${row.toReceive} to receive` : null,
    row.toBill > 0 ? `${row.toBill} to bill` : null,
    row.owed !== null && row.owed > 0 ? `${rupees(row.owed)} to pay` : null,
    row.owed !== null && row.owed < 0 ? `${rupees(-row.owed)} paid ahead` : null,
  ].filter((part): part is string => part !== null);

  return parts.length > 0 ? parts.join(' · ') : 'Nothing waiting';
}

/**
 * When the next payment falls due, said as a counter would — late first, because an MSME supplier
 * is owed interest the day after.
 */
export function dueWords(row: SupplierDeskRow, today: string): string | null {
  if (row.nextDueOn === null || row.nextDueAmount === null) {
    return null;
  }

  const days = dayjs(row.nextDueOn).diff(dayjs(today), 'day');
  const amount = rupees(row.nextDueAmount);

  if (days < 0) {
    return `${amount} was due ${plural(-days, 'day')} ago`;
  }

  return days === 0 ? `${amount} due today` : `${amount} due in ${plural(days, 'day')}`;
}

/** Whether the next payment's day has passed — the row and the To pay step turn amber. */
export function dueIsPast(row: SupplierDeskRow, today: string): boolean {
  return row.msmeOverdue || (row.nextDueOn !== null && row.nextDueOn.slice(0, 10) < today);
}

/**
 * The desk's figures across every supplier it lists (canvas "Purchase order flow", board 0): garments
 * received and not on a bill, and what is owed — money paid ahead to one supplier never lowers what
 * is owed to another. Null to pay for a user who may not see vendor payments.
 */
export function deskTotals(rows: readonly SupplierDeskRow[]): {
  toBill: number;
  toPay: number | null;
  owedTo: number;
} {
  const owing = rows.filter((row) => row.owed !== null && row.owed > 0);

  return {
    toBill: rows.reduce((sum, row) => sum + row.toBill, 0),
    toPay:
      rows.length > 0 && rows.every((row) => row.owed === null)
        ? null
        : owing.reduce((sum, row) => sum + row.owed!, 0),
    owedTo: owing.length,
  };
}

/** One step of where things stand with a supplier — the order tracker's shape, across its orders. */
export interface DeskStep {
  key: 'ordered' | 'received' | 'billed' | 'paid';
  title: string;
  figure: string;
  line: string;
  state: 'done' | 'now' | 'later';
  /** Said in amber — a payment past its day. */
  late: boolean;
}

/**
 * Ordered → received → not on a bill → to pay for the chosen supplier, from the server's desk. To
 * pay is left out for a user who may not see vendor payments.
 */
export function deskSteps(desk: SupplierDesk, today: string): DeskStep[] {
  const { supplier, onOpenOrders, receivedOnOpenOrders, openOrderIds } = desk;
  const steps: DeskStep[] = [
    {
      key: 'ordered',
      title: 'Ordered',
      figure: String(onOpenOrders),
      line:
        onOpenOrders > 0 ? `on ${plural(openOrderIds.length, 'open order')}` : 'nothing on order',
      state: onOpenOrders > 0 ? 'done' : 'later',
      late: false,
    },
    {
      key: 'received',
      title: 'Received',
      figure: String(receivedOnOpenOrders),
      line: supplier.toReceive > 0 ? `${supplier.toReceive} still to come` : 'nothing to come',
      state: supplier.toReceive > 0 ? 'now' : 'done',
      late: false,
    },
    {
      key: 'billed',
      title: 'Not on a bill',
      figure: String(supplier.toBill),
      line: supplier.toBill > 0 ? 'received, waiting for their bill' : 'all billed',
      state: supplier.toBill > 0 ? 'now' : 'done',
      late: false,
    },
  ];

  if (supplier.owed !== null) {
    const owed = supplier.owed;

    steps.push({
      key: 'paid',
      title: 'To pay',
      figure: rupees(Math.max(0, owed)),
      line: dueWords(supplier, today) ?? (owed < 0 ? `${rupees(-owed)} paid ahead` : 'all paid'),
      state: owed > 0 ? 'now' : 'done',
      late: owed > 0 && dueIsPast(supplier, today),
    });
  }

  return steps;
}

/**
 * "Who are you ordering from?" — the first question of a new order (owner, 2026-11-01, canvas
 * "Supplier payment and choice", Supplier A). With nothing typed: the suppliers dealt with lately
 * (most recent first, `recentCount` of them) and the rest by name; typed words narrow both by name
 * or mobile. Read from the desk's every-supplier list.
 */
export function orderSupplierChoices(
  rows: readonly SupplierDeskRow[],
  search: string,
  recentCount = 5,
): { recent: SupplierDeskRow[]; rest: SupplierDeskRow[] } {
  const words = search.trim().toLowerCase();
  const digits = words.replace(/\D/g, '');
  const matching = rows.filter(
    (row) =>
      !words ||
      row.name.toLowerCase().includes(words) ||
      (digits.length >= 3 && (row.mobile ?? '').includes(digits)),
  );
  const recent = matching
    .filter((row) => row.lastActivityOn !== null)
    .sort(
      (a, b) => b.lastActivityOn!.localeCompare(a.lastActivityOn!) || a.name.localeCompare(b.name),
    )
    .slice(0, recentCount);
  const shown = new Set(recent.map((row) => row.vendorId));

  return {
    recent,
    rest: matching
      .filter((row) => !shown.has(row.vendorId))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}

/** "Last dealt with 18 Oct · you owe ₹18,000" — a supplier's row on the choose-who page. */
export function orderSupplierLine(row: SupplierDeskRow): string {
  return [
    row.lastActivityOn ? `last dealt with ${day(row.lastActivityOn)}` : 'nothing ordered yet',
    row.owed === null ? null : row.owed > 0 ? `you owe ${rupees(row.owed)}` : 'nothing owed',
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/** Who the supplier is, in one line under the name. */
export function supplierFacts(row: SupplierDeskRow): string {
  return [
    'Supplier',
    row.isMsme ? 'MSME' : null,
    row.paymentTermDays ? `pays in ${row.paymentTermDays} days` : null,
    row.mobile,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

const eventNoun: Record<SupplierEventKind, string> = {
  Order: 'Order',
  Delivery: 'Delivery',
  Bill: 'Their bill',
  Payment: 'Payment',
};

export const eventKinds = Object.keys(eventNoun) as SupplierEventKind[];

/** The history's filter chips. */
export const eventKindsLabel: Record<SupplierEventKind, string> = {
  Order: 'Orders',
  Delivery: 'Deliveries',
  Bill: 'Bills',
  Payment: 'Payments',
};

/** One paper in the supplier's history: its title line, what it was, and where it stands. */
export function eventLine(event: SupplierEvent): { title: string; detail: string; state: string } {
  const title = `${event.documentNumber} · ${eventNoun[event.kind]}`;

  if (event.cancelled) {
    return { title, detail: day(event.on), state: 'Cancelled' };
  }

  switch (event.kind) {
    case 'Order':
      return {
        title,
        detail: `${day(event.on)} · ${plural(event.units ?? 0, 'garment')}`,
        state: event.pending ? `${event.pending} to come` : 'All in',
      };
    case 'Delivery':
      return {
        title,
        detail: [
          day(event.on),
          plural(event.units ?? 0, 'garment'),
          event.reference ? `challan ${event.reference}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        state: 'Received',
      };
    case 'Bill':
      return {
        title: event.reference ? `${title} ${event.reference}` : title,
        detail: [
          day(event.on),
          event.units ? plural(event.units, 'garment') : null,
          event.dueOn ? `due ${day(event.dueOn)}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        state: event.pending ? `${rupees(event.pending)} left` : 'Paid',
      };
    case 'Payment':
      return {
        title,
        detail: [
          day(event.on),
          event.method ? paymentMethodLabel[event.method] : null,
          event.reference,
        ]
          .filter(Boolean)
          .join(' · '),
        state: rupees(event.amount ?? 0),
      };
  }
}

/**
 * The amounts the Pay screen offers with one tap: the oldest bill, and everything owed. Nothing when
 * nothing is owed; one when there is only one thing to pay.
 */
export function quickPayments(
  ledger: Pick<VendorLedger, 'balance' | 'dues'>,
): { label: string; amount: number }[] {
  if (ledger.balance <= 0) {
    return [];
  }

  const oldest = [...ledger.dues]
    .filter((due) => due.outstanding > 0)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.id - b.id)[0];

  const everything = { label: `Everything · ${rupees(ledger.balance)}`, amount: ledger.balance };

  return oldest && oldest.outstanding < ledger.balance
    ? [
        { label: `Oldest bill · ${rupees(oldest.outstanding)}`, amount: oldest.outstanding },
        everything,
      ]
    : [everything];
}

/**
 * What a payment settles, set against the dues by due date as the server does when no split is
 * chosen — money already on account first — so the screen can say "Paid off" or "Still owed"
 * beside each before it is sent.
 */
export function settles(
  ledger: Pick<VendorLedger, 'dues' | 'onAccount'>,
  amount: number,
): { id: number; kind: string; settled: number; paidOff: boolean }[] {
  let left = Math.max(0, amount) + Math.max(0, ledger.onAccount);

  return [...ledger.dues]
    .filter((due) => due.outstanding > 0)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.id - b.id)
    .map((due) => {
      const settled = Math.min(left, due.outstanding);
      left -= settled;

      return { id: due.id, kind: due.kind, settled, paidOff: settled >= due.outstanding };
    });
}
